import React, { useState } from 'react';
import { useData } from '../../context/DataContext';
import { exportStudentsToCSV } from '../../utils/exportUtils';

export const ParticipationDrilldownModal = ({ filter, onClose, onViewReport }) => {
  const { getScopedStudents } = useData();
  const [statusFilter, setStatusFilter] = useState(filter.initialStatus || 'all'); // 'all' | 'participated' | 'absent'

  if (!filter || !filter.type) return null;

  const getFilteredStudents = () => {
    let list = getScopedStudents();

    if (filter.type === 'school') {
      list = list.filter(s => s.school === filter.value);
    } else if (filter.type === 'grade') {
      list = list.filter(s => s.registeredGrade === filter.value);
    } else if (filter.type === 'gradeSection') {
      list = list.filter(
        s => s.registeredGrade === filter.value.grade && (s.section || 'Unassigned') === filter.value.section
      );
    }

    if (statusFilter === 'participated') {
      list = list.filter(s => s.participated);
    } else if (statusFilter === 'absent') {
      list = list.filter(s => !s.participated);
    }

    return list.sort((a, b) => a.name.localeCompare(b.name));
  };

  const students = getFilteredStudents();

  const getTitle = () => {
    if (filter.type === 'school') return `${filter.value} - Detailed Participation List`;
    if (filter.type === 'gradeSection') return `Grade ${filter.value.grade} - Section ${filter.value.section} - Detailed Participation List`;
    return `Grade ${filter.value} - Detailed Participation List`;
  };

  const handleExport = () => {
    exportStudentsToCSV(students, `Participation_Drilldown_${typeof filter.value === 'object' ? filter.value.grade + '_' + filter.value.section : filter.value}`);
  };

  return (
    <div className="mt-8 pt-8 border-t-2 border-gray-100 select-none">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div className="flex flex-col">
          <h3 className="text-2xl font-bold text-gray-800">{getTitle()}</h3>
          <p className="text-sm text-gray-500">Detailed breakdown of students</p>
        </div>

        <div className="flex items-center gap-4 flex-wrap">
          <button
            onClick={handleExport}
            disabled={students.length === 0}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-bold transition-colors cursor-pointer disabled:opacity-50"
          >
            📥 Export Excel
          </button>

          <div className="flex items-center gap-2">
            <label className="text-sm font-bold text-gray-600">Filter:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border-2 border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none font-medium cursor-pointer"
            >
              <option value="all">All Students</option>
              <option value="participated">Participated Only</option>
              <option value="absent">Absent Only</option>
            </select>
          </div>

          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 font-bold transition-colors cursor-pointer"
          >
            ✕ Close List
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-gray-200 max-h-[460px] overflow-y-auto">
        <table className="w-full text-sm border-collapse">
          <thead className="sticky top-0 bg-gray-50 z-10">
            <tr className="bg-gray-50 text-gray-800 border-b border-gray-200">
              <th className="py-4 px-4 text-left font-bold border-b">Student ID</th>
              <th className="py-4 px-4 text-left font-bold border-b">Name</th>
              <th className="py-4 px-4 text-center font-bold border-b">Grade</th>
              <th className="py-4 px-4 text-center font-bold border-b">Participation Status</th>
              <th className="py-4 px-4 text-center font-bold border-b">Action</th>
            </tr>
          </thead>
          <tbody>
            {students.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center py-20 text-gray-400 font-bold">
                  No students match this filter.
                </td>
              </tr>
            ) : (
              students.map((s) => (
                <tr
                  key={s.id}
                  className={
                    s.participated
                      ? 'hover:bg-gray-50 border-b border-gray-100 transition-colors cursor-pointer'
                      : 'border-b border-gray-100 transition-colors cursor-default bg-gray-50/30'
                  }
                  onClick={() => {
                    if (s.participated) onViewReport(s);
                  }}
                >
                  <td className="py-4 px-4 font-bold text-blue-600">{s.id}</td>
                  <td className="py-4 px-4 font-semibold text-gray-800">{s.name}</td>
                  <td className="py-4 px-4 text-center font-semibold text-gray-700">Grade {s.registeredGrade}</td>
                  <td className="py-4 px-4 text-center">
                    <span
                      className={`px-3 py-1 rounded-lg text-xs font-bold ${
                        s.participated ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {s.status}
                    </span>
                  </td>
                  <td className="py-4 px-4 text-center">
                    {s.participated ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onViewReport(s);
                        }}
                        className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded border border-blue-200 hover:bg-blue-100 font-bold cursor-pointer"
                      >
                        View Report
                      </button>
                    ) : (
                      <span className="text-xs text-red-500 font-bold italic">
                        Please attend the next exam
                      </span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
export default ParticipationDrilldownModal;
